// shared/email-attachments.js — which files ride along with an invoice email,
// and whether they fit.
//
// A company keeps DEFAULT attachments (terms, an insurance certificate) that go
// out with every invoice email; an individual invoice can exclude any of them
// and add EXTRAS of its own. The set an email actually carries is therefore
// (defaults − exclusions) + extras — and that resolution happens in four
// places: the compose screen's Email tab (what the user sees), the manual send,
// the resend, and the recurring generator. One module, so a file the user
// removed on screen cannot still ride the email, and the budget the widget
// enforces is the budget the send refuses over.
//
// Pure: no imports outside shared/, no DOM, no node builtins. Both runtimes.
import { EMAIL_ATTACHMENT_BUDGET_BYTES, MAX_EMAIL_ATTACHMENTS } from "./product-facts.js";
import { cutText } from "./cut-text.js";

export { EMAIL_ATTACHMENT_BUDGET_BYTES, MAX_EMAIL_ATTACHMENTS };

// What a file may be.
//
// An allowlist, not a denylist, but a WIDE one: anything a small business
// would plausibly staple to an invoice. The exclusions are the point —
//
//   .exe .msi .bat .cmd .sh .vbs .scr .jar .app  every mail provider blocks
//                                                these outright; offering them
//                                                would only produce bounces
//   .svg .html .xhtml                            script carriers that arrive
//                                                looking like a document and
//                                                run when opened
//
// Everything else a person actually sends — the paperwork, photos off a phone,
// a short screen recording, a spreadsheet, a zip of the lot — is here.
// Extending the list means extending the server's magic-byte sniffing too
// (server/email-attachments.js), which is why this is data rather than a regex
// someone widens in a hurry.
export const EMAIL_ATTACHMENT_TYPES = [
  // Documents
  { contentType: "application/pdf", extensions: ["pdf"] },
  { contentType: "application/msword", extensions: ["doc"] },
  { contentType: "application/vnd.ms-excel", extensions: ["xls"] },
  { contentType: "application/vnd.ms-powerpoint", extensions: ["ppt"] },
  { contentType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", extensions: ["docx"] },
  { contentType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", extensions: ["xlsx"] },
  { contentType: "application/vnd.openxmlformats-officedocument.presentationml.presentation", extensions: ["pptx"] },
  { contentType: "text/plain", extensions: ["txt"] },
  { contentType: "text/csv", extensions: ["csv"] },
  // Images. HEIC matters: it is what an iPhone camera produces by default.
  { contentType: "image/jpeg", extensions: ["jpg", "jpeg"] },
  { contentType: "image/png", extensions: ["png"] },
  { contentType: "image/webp", extensions: ["webp"] },
  { contentType: "image/gif", extensions: ["gif"] },
  { contentType: "image/heic", extensions: ["heic"] },
  { contentType: "image/heif", extensions: ["heif"] },
  // Video. Not blocked by any mail provider — the binding limit is size, and
  // the budget check says so plainly when a clip is too big. The list covers
  // what the devices a small business owns actually produce: a phone (mp4,
  // mov, 3gp), a screen recorder (mp4, webm, mkv), a dashcam or an older
  // camera (avi, mpeg), and a browser export (webm, ogv).
  { contentType: "video/mp4", extensions: ["mp4"] },
  { contentType: "video/quicktime", extensions: ["mov"] },
  { contentType: "video/webm", extensions: ["webm"] },
  { contentType: "video/x-matroska", extensions: ["mkv"] },
  { contentType: "video/x-msvideo", extensions: ["avi"] },
  { contentType: "video/mpeg", extensions: ["mpeg", "mpg"] },
  { contentType: "video/3gpp", extensions: ["3gp"] },
  { contentType: "video/ogg", extensions: ["ogv"] },
  // Archives
  { contentType: "application/zip", extensions: ["zip"] }
];

export const EMAIL_ATTACHMENT_ACCEPT = EMAIL_ATTACHMENT_TYPES
  .map((t) => t.contentType)
  .join(",");

// The one user-facing sentence for a refused file type, stated once so the
// widget and the route can't drift into naming different lists.
export const EMAIL_ATTACHMENT_TYPE_MESSAGE =
  "That file type can’t be emailed. Choose a document, image, video, or zip.";

export function isAllowedEmailAttachmentType(contentType) {
  const normalized = String(contentType || "").trim().toLowerCase();
  return EMAIL_ATTACHMENT_TYPES.some((t) => t.contentType === normalized);
}

// The extension a file of this type must end in. Not decoration: a mail client
// and a saved-to-disk file are handled by EXTENSION, not by the content type
// the bytes were sniffed as — so a %PDF- file named "terms.html" passes every
// content check and still opens as script-bearing HTML on the recipient's
// machine. The upload rewrites the extension to match the type it verified.
export function emailAttachmentExtensionFor(contentType, filename = "") {
  const type = EMAIL_ATTACHMENT_TYPES.find(
    (t) => t.contentType === String(contentType || "").trim().toLowerCase()
  );
  if (!type) return "";
  const current = String(filename || "").toLowerCase().split(".").pop();
  // Keep the author's spelling when it is already one this type allows
  // (.jpg vs .jpeg), otherwise use the canonical first.
  return type.extensions.includes(current) ? current : type.extensions[0];
}

// A file's name as it is kept and mailed: control characters and quotes
// removed (they are the header-injection vectors, since the name ends up in a
// mail header's Content-Disposition), whitespace collapsed, 120 characters at
// most, and the extension FORCED to match the type the bytes were proven to
// be. That last half is the load-bearing one: a mail client and a
// saved-to-disk file are dispatched by extension, not by what we sniffed, so a
// real PDF named "terms.html" passes every content check and still opens as
// script-bearing HTML on the recipient's machine. Rewriting is quieter than
// refusing and cannot be wrong, the type being proven. Shared so an email
// attachment and a job file (shared/job-files.js) are named by one rule, and
// a phone shows the name the server will keep.
export function cleanAttachmentFilename(filename, contentType) {
  // A path separator would make a folder of it where it is saved, and a
  // leading dot a hidden file. 120 characters, never cut through one
  // (`cutText`): a lone half of an emoji is refused by the database.
  const cleaned = cutText(String(filename || "")
    .replace(/[\u0000-\u001f\u007f"]/g, "")
    .replace(/[\/\\]+/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^[.\s-]+/, ""), 120).trim();
  const ext = emailAttachmentExtensionFor(contentType, cleaned);
  const stem = cleaned.includes(".") ? cleaned.slice(0, cleaned.lastIndexOf(".")).trim() : cleaned;
  const safeStem = stem || "attachment";
  return ext ? `${safeStem}.${ext}` : safeStem;
}

// The set an email for this invoice actually carries, in the order it carries
// them: defaults first (they are the company's standing paperwork), then this
// invoice's own extras. Exclusions are matched by id; an id that no longer
// names a default (the default was deleted after the invoice excluded it) is
// simply inert, which is why the column keeps no foreign key.
//
// Files kept on the invoice's JOB (#1082) come first, as the compose screen
// lists them ("From this job", above the company's defaults): the certificate
// is what the client is looking for. They are counted in the same budget,
// because they ride the same email.
export function resolveEmailAttachmentSet({ defaults = [], excludedIds = [], extras = [], jobFiles = [] } = {}) {
  const excluded = new Set((Array.isArray(excludedIds) ? excludedIds : []).map(String));
  const included = (Array.isArray(defaults) ? defaults : [])
    .filter((d) => d && !excluded.has(String(d.id)));
  return [
    ...(Array.isArray(jobFiles) ? jobFiles : []).filter(Boolean),
    ...included,
    ...(Array.isArray(extras) ? extras : []).filter(Boolean)
  ];
}

export function emailAttachmentSetBytes(set) {
  return (Array.isArray(set) ? set : [])
    .reduce((sum, a) => sum + (Number(a?.sizeBytes) || 0), 0);
}

// Does this set fit the budget? The budget is the files alone: the document's
// PDF is not counted against it (8 October 2026). Until then it was, which
// made the limit a figure nobody could check, "up to 10 MB together" with a
// PDF whose size nobody knows, and held 1 MB back for it on every upload.
//
// WHY THE PDF CAN RIDE ON TOP. The worst email this allows:
//
//   10 MB      the files, at the budget
//   + 2.4 MB   the largest PDF: a 2 MB logo (the most /company/logo takes,
//              a 3 MB data URL) that doesn't compress. Measured: 1.97 MB for
//              a 2 MB noise PNG, the same at 2, 40 or 200 lines. Without a
//              logo an invoice PDF is 20–40 KB.
//   + 0.1 MB   the email's own text and HTML
//   = 12.5 MB raw, × 4/3 for base64 = 16.7 MB as sent.
//
// Against the ceilings that measure the encoded size: Resend refuses above
// 40 MB (resend.com/docs/dashboard/emails/attachments), Gmail takes 25 MB,
// and Microsoft 365 receives 36 MB by default (Exchange Online limits). So
// 8 MB of headroom under the lowest of them, and the old rule's worst case
// was 13.7 MB. `test/email-attachments.test.mjs` does this sum, so raising
// the budget or the logo's cap has to answer it.
export function fitsEmailAttachmentBudget(set) {
  if ((Array.isArray(set) ? set.length : 0) > MAX_EMAIL_ATTACHMENTS) return false;
  return emailAttachmentSetBytes(set) <= EMAIL_ATTACHMENT_BUDGET_BYTES;
}

// The sentence a send refuses with when the set no longer fits — the rare case
// where a default added AFTER this document's set was composed pushed it over,
// or files from the job (#1082) were ticked past it on a phone with no signal.
// The PDF is not counted (8 October 2026, `fitsEmailAttachmentBudget`), so
// neither is it named: the files are what somebody can remove.
//
// Kind-aware, because the fix differs: an invoice has an Email tab to remove a
// file on, and one raised from a job its job's files to untick; a quote and a
// generated recurring invoice have only the company defaults. Pointing
// someone at a control their document doesn't have is worse than saying less.
export function emailAttachmentBudgetMessage(kind = "invoice", { withJobFiles = false } = {}) {
  const mb = EMAIL_ATTACHMENT_BUDGET_BYTES / (1024 * 1024);
  return `These attachments come to more than ${mb} MB. ${whereToRemove(kind, withJobFiles)}`;
}

// The same, when it is the number of files and not their size: a send of
// twelve small certificates used to be told they came to more than 10 MB.
export function emailAttachmentCountMessage(kind = "invoice", { withJobFiles = false } = {}) {
  return `An email can carry up to ${MAX_EMAIL_ATTACHMENTS} attachments. ${whereToRemove(kind, withJobFiles)}`;
}

function whereToRemove(kind, withJobFiles) {
  if (kind !== "invoice") return "Remove a default in Company settings.";
  return withJobFiles
    ? "Untick a file from the job, or remove an attachment on the invoice’s Email tab or a default in Company settings."
    : "Remove an attachment on the invoice’s Email tab, or a default in Company settings.";
}

// Which of the two, if either, a set of files (each with `sizeBytes`) is
// refused with: "" when it fits. Decided from the sizes alone, so a send can
// ask before it reads a single file from storage.
export function emailAttachmentSetRefusal(set, kind = "invoice") {
  const list = (Array.isArray(set) ? set : []).filter(Boolean);
  const withJobFiles = list.some((a) => a.jobFileId);
  if (list.length > MAX_EMAIL_ATTACHMENTS) return emailAttachmentCountMessage(kind, { withJobFiles });
  if (emailAttachmentSetBytes(list) > EMAIL_ATTACHMENT_BUDGET_BYTES) return emailAttachmentBudgetMessage(kind, { withJobFiles });
  return "";
}

// ── Whether one more file fits ───────────────────────────────────────────────
//
// The two refusals, stated ONCE. They used to live on the server route with a
// hand copy in the web's widget, and the copy had already drifted: it wrote
// the room left in a different format.
// The phone checks before it uploads (#636), so it needs the same sentence
// the server would have sent — which means the sentence lives here.

export const EMAIL_ATTACHMENT_TOO_MANY_MESSAGE =
  `An email can carry up to ${MAX_EMAIL_ATTACHMENTS} attachments. Remove one first.`;

// Room left, as the refusal writes it: KB under a megabyte, MB to one place
// above ("3.5 MB", "4 MB").
export function formatAttachmentRoom(bytes) {
  const left = Math.max(0, Number(bytes) || 0);
  if (left < 1024 * 1024) return `${Math.max(0, Math.round(left / 1024))} KB`;
  const mb = left / (1024 * 1024);
  const rounded = Math.round(mb * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)} MB`;
}

// Says how much room is left rather than restating the ceiling, so the reader
// knows whether to compress this file or remove another.
export function emailAttachmentTooBigMessage(remainingBytes) {
  return `That file is too big for this email. There’s ${formatAttachmentRoom(remainingBytes)} left. Remove an attachment, or choose a smaller file.`;
}

// Whether a file of `sizeBytes` and `contentType` may join an email that
// already carries `count` files of `bytes` in all: the refusal sentence, or ""
// when it fits. `contentType` is optional — the server has sniffed the bytes
// by the time it asks, and passes none.
export function emailAttachmentRefusal({ count = 0, bytes = 0, sizeBytes = 0, contentType } = {}) {
  if (contentType !== undefined && !isAllowedEmailAttachmentType(contentType)) return EMAIL_ATTACHMENT_TYPE_MESSAGE;
  if (Number(count) + 1 > MAX_EMAIL_ATTACHMENTS) return EMAIL_ATTACHMENT_TOO_MANY_MESSAGE;
  const room = EMAIL_ATTACHMENT_BUDGET_BYTES - (Number(bytes) || 0);
  if ((Number(sizeBytes) || 0) > room) return emailAttachmentTooBigMessage(room);
  return "";
}
